
import { handleRetrieveSingleWebinar } from "../../../../../redux/specialist-slices/webinar.slice";
import SkeletonImage from "../../../../../components/ui/SkeletonImage";
import { FILE_PATH_URI } from "../../../../../utils/helper";
import { useDispatch, useSelector } from "react-redux";
import { Fragment, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import moment from "moment";

import {
  Alert, Box, Container, Divider, Grid2 as Grid, List,
  ListItem, ListItemText, Skeleton, Stack, Typography
} from "@mui/material";

const WebinarsDetails = () => {
  const dispatch = useDispatch();
  const { webinarId } = useParams();
  const [webinar, setWebinar] = useState({});

  const { loadings } = useSelector((state) => state.otherSlice);

  const handleRetrieve = async () => {
    const { webinar } =
      (await dispatch(handleRetrieveSingleWebinar(webinarId)).unwrap()) || {};
    if (webinar) {
      setWebinar(webinar);
    }
  };

  useEffect(() => {
    handleRetrieve();
    // eslint-disable-next-line
  }, [webinarId]);

  return (
    <Fragment>
      <Box sx={{ py: { xs: 1, lg: 1 }, bgcolor: "#f1f1f1" }}>
        <Container maxWidth={false}>
          <Box sx={{ ml: "auto" }}>
            {loadings?.retrieveSingle ? (
              <Skeleton
                sx={{
                  width: "100%",
                  minHeight: 350,
                  transform: "none",
                }}
              />
            ) : !webinar ? (
              <Alert
                sx={{
                  fontSize: "20px",
                  fontWeight: "500",
                  alignItems: "center",
                  fontFamily: "Poppins",
                  ".MuiAlert-message": { p: 0 },
                  ".MuiAlert-icon": { fontSize: "28px" },
                }}
                color="error"
                severity="error"
              >
                No Records Found!
              </Alert>
            ) : (
              <>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                  <Stack
                    direction="row"
                    spacing={2}
                    sx={{ alignItems: "center", flexWrap: { xs: "wrap" } }}
                    divider={<Divider flexItem orientation="vertical" />}
                  ></Stack>

                  <Stack
                    direction="row"
                    spacing={2}
                    sx={{ alignItems: "center", flexWrap: { xs: "wrap" } }}
                    divider={<Divider flexItem orientation="vertical" />}
                  >
                    <Typography
                      color="other.color2"
                      fontSize={{ xs: 14, md: 16 }}
                    >
                      {webinar?.seats?.booked || 0} Seats booked
                    </Typography>
                    <Typography
                      color={
                        webinar?.seats?.available < 10
                          ? "error.main"
                          : "other.color2"
                      }
                      fontSize={{ xs: 14, md: 16 }}
                    >
                      {webinar?.seats?.available < 10 && "Hurry up! "}
                      {webinar?.seats?.available || 0} Seats left
                    </Typography>
                  </Stack>
                </Box>

                <Divider sx={{ mt: 2 }} />

                <Typography
                  sx={{
                    my: 2,
                    width: "100%",
                    overflow: "hidden",
                    WebkitLineClamp: 1,
                    color: "other.color1",
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                  }}
                  fontSize={28}
                  fontWeight="700"
                >
                  {webinar?.details?.title}
                </Typography>

                <Box
                  sx={{
                    overflow: "hidden",
                    borderRadius: "6px",
                    width: "60%",
                  }}
                >
                  <SkeletonImage
                    sx={{
                      width: "100%",
                      height: "auto",
                      maxHeight: "500px",
                      objectFit: "contain"
                    }}
                    src={FILE_PATH_URI + webinar?.details?.coverImage?.path}
                  />
                </Box>

                <Box
                  sx={{ color: "other.color1", mt: 3 }}
                  dangerouslySetInnerHTML={{
                    __html: webinar?.details?.description,
                  }}
                />

                {/* Additional Webinar Details */}
                <Box sx={{ mt: 3 }}>
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography
                        fontSize={20}
                        fontWeight="600"
                        color="other.color1"
                        sx={{ mb: 2 }}
                      >
                        Webinar Details
                      </Typography>
                      <List sx={{ width: "100%" }}>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemText
                            primary="Number of Seats"
                            secondary={webinar?.details?.numberOfSeats}
                          />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemText
                            primary="Target Audience"
                            secondary={webinar?.details?.targetAudience?.join(
                              ", "
                            )}
                          />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemText
                            primary="Format"
                            secondary={webinar?.format?.format}
                          />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemText
                            primary="Access"
                            secondary={webinar?.format?.access}
                          />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemText
                            primary="Link"
                            secondary={
                              <Typography
                                component="a"
                                href={webinar?.resources?.webinarLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  color: "primary.main",
                                  textDecoration: "underline",
                                  whiteSpace: "normal",
                                  wordBreak: "break-all",
                                  display: "inline-block",
                                }}
                              >
                                {webinar?.resources?.webinarLink}
                              </Typography>
                            }
                          />
                        </ListItem>

                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemText
                            primary="Status"
                            secondary={
                              webinar?.status === "rejected"
                                ? "Revision Requested"
                                : webinar?.status
                            }
                            sx={{
                              ".MuiListItemText-secondary": {
                                textTransform: "capitalize",
                              },
                            }}
                          />
                        </ListItem>

                        {webinar?.reason && (
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemText
                              primary="Reason"
                              secondary={webinar?.reason}
                            />
                          </ListItem>
                        )}
                      </List>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography
                        fontSize={20}
                        fontWeight="600"
                        color="other.color1"
                        sx={{ mb: 2 }}
                      >
                        Schedule
                      </Typography>
                      <List>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemText
                            primary="Date"
                            secondary={moment(
                              webinar?.schedule?.date
                            ).format("MMM DD YYYY")}
                          />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemText
                            primary="Start Time"
                            secondary={moment(
                              webinar?.schedule?.startTime
                            ).format("MMM DD YYYY [at] hh:mm A")}
                          />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemText
                            primary="Duration"
                            secondary={`${webinar?.schedule?.duration} minutes`}
                          />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemText
                            primary="Time Zone"
                            secondary={webinar?.schedule?.timeZone?.toUpperCase()}
                          />
                        </ListItem>
                      </List>
                    </Grid>
                  </Grid>
                </Box>
              </>
            )}
          </Box>
        </Container>
      </Box>
    </Fragment>
  );
};

export default WebinarsDetails;